import { ActionCommand, CancellationTypeValue } from '@nextforce/api'
import { Input, ListItem, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useCallback, useEffect, useState } from 'react'
import { FlatList, Pressable, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CommandButton } from 'shared/components/command_button'

interface Props {
  cancellationData: CancellationTypeValue[]
  onButtonPress: (cancelTypeValue: CancellationTypeValue) => Promise<void>
  command: ActionCommand
}

export default function CancelShiftView({
  cancellationData,
  onButtonPress,
  command,
}: Props) {
  const styles = useStyles()

  const [selectedType, setSelectedType] = useState<
    CancellationTypeValue | undefined
  >()

  const onChangeReason = useCallback(
    (inputText: string) =>
      selectedType && setSelectedType({ ...selectedType, reason: inputText }),
    [selectedType]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View>
          <Text style={styles.title}>{t('whoIsCanceling')}</Text>
          <FlatList
            style={styles.list}
            data={cancellationData}
            keyExtractor={item => String(item.id)}
            renderItem={({ item }) => (
              <CancellationTypeRow
                cancellationTypeValue={item}
                selected={selectedType && item.id === selectedType.id}
                onPress={() => setSelectedType(item)}
                onChangeReason={onChangeReason}
              />
            )}
          />
          <CommandButton
            command={command}
            disabled={isButtonDisabled(selectedType)}
            onPress={() => onButtonPress(selectedType!)}
            containerStyle={styles.buttonContainer}
          />
        </View>
      </View>
    </SafeAreaView>
  )
}

function isButtonDisabled(
  selectedType: CancellationTypeValue | undefined
): boolean {
  if (selectedType) {
    if (selectedType?.type === 'MANAGER_INITIATED_FOR_HELPER') {
      if (selectedType.reason) {
        return false
      } else {
        return true
      }
    } else {
      return false
    }
  } else {
    return true
  }
}

interface CancellationTypeRowProps {
  cancellationTypeValue: CancellationTypeValue
  selected?: boolean
  onPress(): void
  reason?: string
  onChangeReason: (inputText: string) => void | undefined
}

function CancellationTypeRow({
  cancellationTypeValue,
  selected,
  onPress,
  reason,
  onChangeReason,
}: CancellationTypeRowProps) {
  const [text, setText] = useState(reason ?? '')

  function onChangeText(inputText: string) {
    setText(inputText)
    onChangeReason(inputText)
  }

  const isCrewCancelled =
    selected && cancellationTypeValue.type === 'MANAGER_INITIATED_FOR_HELPER'

  useEffect(() => {
    if (!isCrewCancelled) {
      setText('')
    }
  }, [isCrewCancelled])
  const styles = useStyles()
  return (
    <>
      <Pressable onPress={onPress}>
        <ListItem containerStyle={styles.listItem}>
          <ListItem.CheckBox
            onPress={onPress}
            checkedIcon="dot-circle-o"
            uncheckedIcon="circle-o"
            checked={selected ?? false}
          />
          <ListItem.Content>
            <ListItem.Title>{cancellationTypeValue.text}</ListItem.Title>
            {isCrewCancelled && (
              <ListItem.Subtitle>{t('crewCanceled')}</ListItem.Subtitle>
            )}
          </ListItem.Content>
        </ListItem>
      </Pressable>
      {isCrewCancelled && (
        <View style={styles.inputContainer}>
          <Input
            style={styles.input}
            placeholder={t('crewCancelReason')}
            value={text}
            onChangeText={inputText => onChangeText(inputText)}
            multiline
          />
        </View>
      )}
    </>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.grey5,
  },

  title: {
    margin: 10,
    marginBottom: 0,
    color: theme.colors?.grey1,
    fontSize: 15,
  },

  list: {
    paddingVertical: 10,
  },

  listItem: {
    marginTop: 5,
  },
  input: {
    height: 100,
    textAlignVertical: 'top',
    marginVertical: 5,
    borderColor: theme.colors?.grey4,
    color: 'black',
  },
  inputContainer: {
    backgroundColor: 'white',
    margin: 10,
  },
  buttonContainer: {
    width: '100%',
    padding: 10,
  },
}))
